// Utility voor .ics-download en Google Agenda-links per event.
// - Hele-dag events: DTSTART;VALUE=DATE + DTEND;VALUE=DATE (DTEND exclusief per RFC5545)
// - Meerdaagse events: DTEND = einddatum + 1 dag (exclusief eindpunt)
// - Als er een tijd is (HH:MM of HH:MM-HH:MM), gebruiken we die op de begindatum.

const pad = (n) => String(n).padStart(2, "0");

const ymd = (dateStr) => {
  // dateStr = "YYYY-MM-DD"
  const [y, m, d] = dateStr.split("-").map(Number);
  return { y, m, d };
};

const plusDays = (dateStr, days) => {
  const { y, m, d } = ymd(dateStr);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}`;
};

const compact = (dateStr) => {
  const { y, m, d } = ymd(dateStr);
  return `${y}${pad(m)}${pad(d)}`;
};

const parseTimeRange = (time) => {
  // Ondersteunt "HH:MM" of "HH:MM-HH:MM" of "HH.MM" of "HH.MM-HH.MM"
  if (!time) return null;
  const t = time.replace(/\./g, ":").trim();
  const m = t.match(/^(\d{1,2}):(\d{2})(?:\s*-\s*(\d{1,2}):(\d{2}))?/);
  if (!m) return null;
  const [_, h1, mi1, h2, mi2] = m;
  return {
    start: `${pad(h1)}${pad(mi1)}00`,
    end: h2 ? `${pad(h2)}${pad(mi2)}00` : null,
  };
};

const escapeIcs = (text) =>
  String(text || "")
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

/**
 * Bouw start/eind strings + all-day flag voor een event.
 * Retourneert { start, end, allDay } — waarden ALTIJD als compact YYYYMMDD of YYYYMMDDTHHMMSS.
 */
export const eventTimeRange = (ev) => {
  if (!ev?.date) return null;
  const startDate = ev.date; // "YYYY-MM-DD"
  const endDate = ev.end_date || ev.date;
  const time = parseTimeRange(ev.time);
  if (time) {
    // Tijd bekend → gebruik lokale tijd (Europe/Amsterdam)
    const start = `${compact(startDate)}T${time.start}`;
    // Default eind = start + 2u; als er einduur is → gebruik dat.
    let endStr;
    if (time.end) {
      endStr = `${compact(startDate)}T${time.end}`;
    } else {
      // Voeg 2 uur toe
      const [h, m] = [
        parseInt(time.start.substring(0, 2), 10),
        parseInt(time.start.substring(2, 4), 10),
      ];
      const newH = (h + 2) % 24;
      const rollover = h + 2 >= 24 ? 1 : 0;
      const endDay = rollover ? plusDays(startDate, 1) : compact(startDate);
      endStr = `${endDay}T${pad(newH)}${pad(m)}00`;
    }
    return { start, end: endStr, allDay: false };
  }
  // Geen tijd → hele dag(en)
  const start = compact(startDate);
  const end = plusDays(endDate, 1); // DTEND exclusief
  return { start, end, allDay: true };
};

/** Genereer .ics content voor één event. */
export const buildIcs = (ev, opts = {}) => {
  const range = eventTimeRange(ev);
  if (!range) return null;
  const uid = `${ev.slug || "event"}-${range.start}@lemmer-leeft.nl`;
  const dtstamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
  const title = escapeIcs(opts.title || ev.title || "Evenement");
  const desc = escapeIcs(
    [opts.description || ev.description || "", ev.link ? `Meer info: ${ev.link}` : ""]
      .filter(Boolean)
      .join("\n"),
  );
  const location = escapeIcs(opts.location || ev.location || "");
  const dtStart = range.allDay
    ? `DTSTART;VALUE=DATE:${range.start}`
    : `DTSTART;TZID=Europe/Amsterdam:${range.start}`;
  const dtEnd = range.allDay
    ? `DTEND;VALUE=DATE:${range.end}`
    : `DTEND;TZID=Europe/Amsterdam:${range.end}`;
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Lemmer Leeft//Agenda//NL",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    dtStart,
    dtEnd,
    `SUMMARY:${title}`,
    location ? `LOCATION:${location}` : "",
    desc ? `DESCRIPTION:${desc}` : "",
    ev.link ? `URL:${ev.link}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
};

/** Trigger .ics download in de browser. */
export const downloadIcs = (ev, opts = {}) => {
  const content = buildIcs(ev, opts);
  if (!content) return;
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(ev.slug || "event")}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Google Calendar TEMPLATE URL. */
export const buildGoogleCalendarUrl = (ev, opts = {}) => {
  const range = eventTimeRange(ev);
  if (!range) return null;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: opts.title || ev.title || "Evenement",
    dates: `${range.start}/${range.end}`,
    details: [
      opts.description || ev.description || "",
      ev.link ? `Meer info: ${ev.link}` : "",
    ]
      .filter(Boolean)
      .join("\n\n"),
    location: opts.location || ev.location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
